// Insurance A/R — the follow-up schedule (D117, staging fix §7). UI-only over
// mock data (§8): ELVA follows up once the payer's usual time has passed, then
// about every two weeks. These helpers generate every round on that schedule so
// the rounds that show are the rounds that really ran, and so a claim that is
// still being worked shows the actual next scheduled date (never a vague
// "about 2 weeks", never "follow-up 4 of 3").

import { MOCK_NOW } from '@/components/lists';
import type { ArTry } from './types';

const INTERVAL_DAYS = 14;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function addDays(base: Date, n: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
}

/** "Mon D" (and ", YYYY" when it isn't the mock year). */
export function dateLabel(d: Date): string {
  const base = `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}`;
  return d.getFullYear() === MOCK_NOW.getFullYear() ? base : `${base}, ${String(d.getFullYear())}`;
}

/**
 * The dates "keep going" would run if a person chose it (D159: another full
 * set). The first is one interval after the last round; then every two weeks.
 */
export function keepGoingDates(count: number): string[] {
  const out: string[] = [];
  for (let k = 0; k < count; k++) out.push(dateLabel(addDays(MOCK_NOW, INTERVAL_DAYS - 1 + k * INTERVAL_DAYS)));
  return out;
}

/** Today in the mock, as "Oct 16". */
export function todayLabel(): string {
  return dateLabel(MOCK_NOW);
}

/** How many days ago the most recent follow-up ran — deterministic per claim. */
function lastRoundDaysBack(seed: number): number {
  return 5 + (Math.abs(seed) % 7); // 5–11 days ago
}

/**
 * The date each follow-up round ran, oldest → newest. The newest is a few days
 * back and earlier ones are one interval apart before it, so a claim that has
 * run `rounds` rounds has exactly `rounds` dated follow-ups in its past.
 */
export function followUpDates(rounds: number, seed: number): string[] {
  const last = lastRoundDaysBack(seed);
  const out: string[] = [];
  for (let k = 1; k <= rounds; k++) {
    const back = last + (rounds - k) * INTERVAL_DAYS;
    out.push(dateLabel(addDays(MOCK_NOW, -back)));
  }
  return out;
}

/**
 * The next scheduled check for a claim still being worked: one interval after
 * the most recent round, which always lands in the future relative to MOCK_NOW.
 */
export function nextCheckDate(seed: number): string {
  const last = lastRoundDaysBack(seed);
  return dateLabel(addDays(MOCK_NOW, INTERVAL_DAYS - last)); // 3–9 days out
}

/**
 * The tries for a generated late claim: "Sent.", then one dated status-check
 * per round that really ran, on the real schedule. Every round the claim claims
 * to have run appears here, so the round count and the tries always agree.
 */
export function scheduledLateTries(idLower: string, short: string, sent: string, rounds: number, seed: number): ArTry[] {
  const dates = followUpDates(rounds, seed);
  const tries: ArTry[] = [
    { date: sent, icon: 'ok', head: 'Sent.', body: `Accepted by the clearinghouse. ${short} received it the same day.`, logId: `${idLower}-1` },
  ];
  dates.forEach((d, idx) => {
    const k = idx + 1;
    const last = k === rounds;
    tries.push({
      date: d,
      icon: last ? 'x' : 'dot',
      head: `Follow-up ${String(k)} · status check.`,
      body: last
        ? `${short}: still “in process”, now past its usual time.`
        : `${short}: “in process”. Nothing missing.`,
      logId: `${idLower}-${String(k + 1)}`,
    });
  });
  return tries;
}

/** The real round count a claim has actually run, read from its tries. */
export function countRounds(tries: ArTry[]): number {
  let max = 0;
  for (const t of tries) {
    const m = /Follow-up (\d+)/.exec(t.head);
    if (m?.[1]) max = Math.max(max, Number(m[1]));
  }
  return max;
}
